/**
 * mobileTrainingEstimate — pure elapsed-time → possible-completion estimator for
 * the mobile return prompt.
 *
 * IMPORTANT: this is "time away from OptiBay", NEVER verified watch time. The
 * percentage is an UPPER BOUND on *possible* completion (you cannot have watched
 * more than the time you were away), capped at 100. It never drives
 * auto-completion — a human must explicitly self-attest.
 */

export interface MobileTrainingEstimate {
  /** Seconds between the "Open training" tap and returning to OptiBay. */
  elapsedSeconds: number;
  /** Known artifact duration in seconds, or null when unknown. */
  durationSeconds: number | null;
  /** floor(elapsed/duration*100), capped at 100; null when duration unknown. */
  estimatedCompletionPct: number | null;
}

export function computeMobileTrainingEstimate(args: {
  launchedAtMs: number;
  returnMs: number;
  durationMinutes: number | null;
}): MobileTrainingEstimate {
  const rawElapsed = Math.floor((args.returnMs - args.launchedAtMs) / 1000);
  const elapsedSeconds = rawElapsed > 0 ? rawElapsed : 0;

  const durationSeconds =
    typeof args.durationMinutes === 'number' && args.durationMinutes > 0
      ? args.durationMinutes * 60
      : null;

  const estimatedCompletionPct =
    durationSeconds && durationSeconds > 0
      ? Math.min(100, Math.floor((elapsedSeconds / durationSeconds) * 100))
      : null;

  return { elapsedSeconds, durationSeconds, estimatedCompletionPct };
}

/**
 * Human, approximate label for the copy — e.g. "7 minutes" / "45 seconds".
 * The surrounding copy supplies the word "about", so this returns the bare
 * quantity.
 */
export function formatAwayDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  if (s < 60) return `${s} second${s === 1 ? '' : 's'}`;
  const minutes = Math.round(s / 60);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/** Known artifact duration label (minutes) — e.g. "10 minutes". */
export function formatKnownDuration(durationMinutes: number): string {
  const m = Math.max(1, Math.round(durationMinutes));
  return `${m} minute${m === 1 ? '' : 's'}`;
}
